import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import Spinner from "../../../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../../../common/ErrorComponent";
import {
  backtoSevenDays,
  displayDateTime,
} from "../../../../../../../../utils/displayTime";
import CustomTimeStamp from "../../../../../../../../generics/TimeStamp/customTimeStamp";
import useI18n from "../../../../../../../../i18n/useI18n";
import { appChartPayload } from "../../../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../../../config/api-config";
import NoDataAvailable from "../../../../../../../../common/NoDataAvailable";
import ADUsageLineChart from "../../../../genericComponents/ADUsageLineChart";
import {
  isRenderRequired,
  getProcessedChartData,
} from "../../../../common/adUtils";
import css from "../../../../common/anomalyMagneticStyle.less";

const ApplicationsChart = (props) => {
  const { tViews } = useI18n("views");
  const latestRequest = useRef(0);
  const [chartLoader, setChartLoader] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [timePeriod, setTimePeriod] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [noData, setNoData] = useState([]);
  const [removedList, setRemovedList] = useState([]);
  const [reSelectApps, addReSelectApps] = useState([]);
  const [apiAppList, setAPIAppList] = useState([]);

  useMemo(() => {
    // reset state
    setChartData(null);
    setRemovedList([]);
    addReSelectApps([]);
    setNoData(null);
    setAPIAppList([]);
    setChartError(null);
  }, [props.circuit_name]);

  const isRender = useMemo(() => {
    return isRenderRequired(
      props.prevAppUsageList, // prev list
      props.appList, // current
      props.setPrevAppUsgList, // Update prev
      setNoData, // func for no data
      setRemovedList, // func for removed list
      removedList, // current removed list
      addReSelectApps, // func for re-select circuit
      apiAppList, // API circuit list
      setAPIAppList, // func to set API circuit list
    );
  }, [props.appList]);

  useEffect(() => {
    const runEffect = async () => {
      if (isRender && props.appList.length > 0) {
        const abortController = new AbortController();
        latestRequest.current += 1;
        const requestId = latestRequest.current;
        try {
          let timePeriod = backtoSevenDays();
          setTimePeriod(timePeriod);
          setChartLoader(true);
          let payload = appChartPayload(
            props.circuit_name,
            props.appList,
            timePeriod,
          );
          let res = await ApiService.getAppChartData(payload, {
            signal: abortController.signal,
          });
          if (latestRequest.current !== requestId) return;
          if (!res.errorObject) {
            let processedData = getProcessedChartData(
              res.data.data,
              "apps",
              timePeriod,
            );
            setChartData(processedData);
            props.setPrevAppUsgList(props.appList);
            setAPIAppList(props.appList);
          } else {
            setChartError(res.errorObject);
          }
        } catch (error) {
          // Handle error
          if (error.name === "AbortError") return; // Ignore abort error
          // Only process if this is the latest request
          if (latestRequest.current !== requestId) return;
          setChartError(error);
        } finally {
          setChartLoader(false);
        }
      }
    };

    runEffect();
  }, [isRender]);

  return (
    <div
      className={css["ad-app-chart"]}
      role="region"
      aria-label={tViews("usageBasedAD.applicationsChartLabel")}
    >
      <div aria-live="polite">
        {chartLoader ? (
          <Spinner />
        ) : chartError ? (
          <ErrorComponent error={chartError} />
        ) : noData ? (
          <NoDataAvailable />
        ) : (
          <div>
            {timePeriod && (
              <div className="customt-timestamp">
                <CustomTimeStamp
                  prevTime={displayDateTime(timePeriod[0])}
                  currentTime={displayDateTime(timePeriod[1])}
                />
                <div className="ad-timePeriodTxt">
                  {tViews("app360.afHeatmapTimePriodText")}
                </div>
              </div>
            )}
            {chartData?.data?.length > 0 && (
              <div>
                <ADUsageLineChart
                  id="app-usage-trend-chart"
                  data={chartData.data}
                  anomaly_legend={false}
                  selectedHour={props.selectedHour}
                  removedList={removedList}
                  addedCircuit={reSelectApps}
                  palette={chartData.palette}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ApplicationsChart.propTypes = {
  circuit_name: PropTypes.string.isRequired,
  appList: PropTypes.array,
  selectedHour: PropTypes.string,
  setPrevAppUsgList: PropTypes.func,
  prevAppUsageList: PropTypes.array,
};

export default ApplicationsChart;
